import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Checkbox,
    CircularProgress,
    Alert,
    Tooltip,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../../../contexts/UserContext';
import { loginRequest } from '../../../authConfig';
import styles from '../../../styles/purchaseInvoices/invoiceManagement/GoodsReceiptCreationScreenStyles';
import FileViewer from './FileViewer';

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const rtlTheme = createTheme({
    direction: 'rtl',
});

const VAT_RATE = 0.17;


const GoodsReceiptCreationScreen = ({
    orderData,
    receiptDate,
    invoice,
    onBack,
    onSuccess
}) => {
    const { instance, accounts } = useMsal();
    const { user } = useUser();
    
    const [items, setItems] = useState([]);
    const [receivingWarehouse, setReceivingWarehouse] = useState(''); // מחסן מקבל
    const [deletedItems, setDeletedItems] = useState(new Set());
    const [isCreating, setIsCreating] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [error, setError] = useState('');
    const [createdDocNo, setCreatedDocNo] = useState(null);
    const [invoiceContent, setInvoiceContent] = useState(null);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [invoiceViewerError, setInvoiceViewerError] = useState('');
    const [showInvoicePanel, setShowInvoicePanel] = useState(false);

    useEffect(() => {
        if (orderData?.items) {
            setItems(orderData.items.map((item, index) => ({
                ...item,
                id: index,
                quantity: item.quantity || 0,
                price: item.unitPrice || 0,
                userModified: false,
            })));
        }
        if (orderData?.warehouse) {
            setReceivingWarehouse(orderData.warehouse);
        }
    }, [orderData]);

    const activeItems = useMemo(() => {
        return items.filter((_, index) => !deletedItems.has(index));
    }, [items, deletedItems]);

    const total = useMemo(() => {
        return activeItems.reduce((sum, item) => {
            const lineTotal = item.userModified
                ? (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)
                : (parseFloat(item.totalPrice) || 0);
            return sum + lineTotal;
        }, 0);
    }, [activeItems]);

    const vatRate = useMemo(() => {
        if (orderData?.totalPrice != null && orderData?.netPrice > 0) {
            return (orderData.totalPrice / orderData.netPrice) - 1;
        }
        return VAT_RATE;
    }, [orderData]);

    const totalWithVat = useMemo(() => total * (1 + vatRate), [total, vatRate]);

    const handleViewInvoice = async () => {
        setShowInvoicePanel(true);
        if (invoiceContent) return;

        if (!invoice?.fileName) return;

        setIsLoadingInvoice(true);
        setInvoiceViewerError('');
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/file/base64`,
                { fileName: invoice.fileName },
                { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
            );

            const fileData = response.data;
            setInvoiceContent(fileData || null);
            if (!fileData) setInvoiceViewerError('לא נמצא קובץ חשבונית');
        } catch (err) {
            setInvoiceViewerError('שגיאה בטעינת קובץ החשבונית');
        } finally {
            setIsLoadingInvoice(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        setItems(prevItems => {
            const newItems = [...prevItems];
            newItems[index] = {
                ...newItems[index],
                [field]: value,
                ...(field === 'quantity' || field === 'price' ? { userModified: true } : {}),
            };
            return newItems;
        });
    };

    const handleDeleteItem = (index) => {
        setDeletedItems(prev => new Set([...prev, index]));
    };

    const handleRestoreItem = (index) => {
        setDeletedItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    const handleCreateGoodsReceipt = async () => {
        if (activeItems.length === 0) {
            setError('יש לבחור לפחות שורה אחת');
            return;
        }

        if (!receivingWarehouse.trim()) {
            setError('יש להזין מחסן מקבל לפני יצירת קבלת סחורה');
            return;
        }

        setError('');
        setIsCreating(true);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const payload = {
                orderName: orderData.orderName,
                receiptDate: receiptDate,
                supplierNumber: orderData.supplierNumber,
                branchName: orderData.branchName,
                receivingWarehouse: receivingWarehouse, 
                booknum: invoice?.booknum || '', 
                finalize: true, 
                items: activeItems.map(item => ({
                    orderName: orderData.orderName,
                    itemCode: item.itemCode,
                    kline: item.kline, // KLINE from original order
                    quantity: parseFloat(item.quantity) || 0,
                    price: parseFloat(item.price) || 0,
                    budgetCode: item.budgetCode || '',
                    exchange: item.exchangeRate || 1,
                    lineNumber: item.kline || item.lineNumber
                })),
                invoiceId: invoice?.id
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/create`,
                payload,
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
                }
            );

            const result = response.data.data;
            setCreatedDocNo(result?.docNo);

            if (result?.finalized || result?.status === 'סופית') {
                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess(result.docNo);
                    }, 1500);
                }
            } else if (result?.finalizeError) {
                setError(`קבלת סחורה נוצרה כטיוטא. שגיאה בסיפור: ${result.finalizeError}`);
            }
        } catch (err) {
            console.error('Error creating goods receipt:', err);
            setError(err.response?.data?.message || err.message || 'שגיאה ביצירת קבלת סחורה');
        } finally {
            setIsCreating(false);
        }
    };

    const handleFinalizeGoodsReceipt = async () => {
        if (!createdDocNo) {
            setError('יש ליצור קבלת סחורה קודם');
            return;
        }

        setError('');
        setIsFinalizing(true);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            await axios.post(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/${createdDocNo}/finalize`,
                {},
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
                }
            );

            if (onSuccess) {
                setTimeout(() => {
                    onSuccess(createdDocNo);
                }, 1500);
            }
        } catch (err) {
            console.error('Error finalizing goods receipt:', err);
            setError(err.response?.data?.message || err.message || 'שגיאה בסיפור קבלת סחורה');
        } finally {
            setIsFinalizing(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS'
        }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('he-IL');
    };

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={rtlTheme}>
                <Box dir="rtl" sx={styles.splitWrapper}>

                    <Box sx={{ ...styles.formPanel, flex: showInvoicePanel ? '0 0 56%' : '1' }}>
                        <Box sx={styles.header}>
                            <Box sx={styles.headerLeft}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowForwardIcon />}
                                    onClick={onBack}
                                    sx={styles.backButton}
                                    disabled={isCreating || isFinalizing}
                                >
                                    חזרה
                                </Button>
                                <Box sx={styles.titleBox}>
                                    <Box sx={styles.titleIcon}>
                                        <InventoryIcon />
                                    </Box>
                                    <Box>
                                        <Typography sx={styles.title}>
                                            יצירת קבלת סחורה
                                        </Typography>
                                        <Typography sx={styles.subtitle}>
                                            עבור הזמנה <Box component="span" sx={styles.orderBadge}>{orderData?.orderName}</Box>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            {invoice && (
                                <Button
                                    variant="outlined"
                                    startIcon={showInvoicePanel ? <CloseIcon /> : <VisibilityIcon />}
                                    onClick={() => showInvoicePanel ? setShowInvoicePanel(false) : handleViewInvoice()}
                                    sx={showInvoicePanel ? styles.hideInvoiceButton : styles.viewInvoiceButton}
                                >
                                    {showInvoicePanel ? 'סגור חשבונית' : 'צפה בחשבונית'}
                                </Button>
                            )}
                        </Box>

                        {createdDocNo && (
                            <Box sx={styles.createdDocInfo}>
                                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.5rem' }} />
                                <Typography sx={styles.createdDocText}>
                                    קבלת סחורה נוצרה ואושרה בהצלחה כסופית - מספר מסמך: <strong>{createdDocNo}</strong>
                                </Typography>
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={styles.errorAlert} onClose={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        <Paper sx={styles.infoCard}>
                            <Box sx={styles.infoGrid}>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>תאריך</Typography>
                                    <Typography sx={styles.infoValue}>{formatDate(receiptDate)}</Typography>
                                </Box>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>מספר הזמנה</Typography>
                                    <Typography sx={styles.infoValue}>{orderData?.orderName}</Typography>
                                </Box>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>מס' ספק</Typography>
                                    <Typography sx={styles.infoValue}>{orderData?.supplierNumber}</Typography>
                                </Box>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>שם ספק</Typography>
                                    <Typography sx={styles.infoValue}>
                                        {orderData?.supplierName || orderData?.supplierNumber}
                                    </Typography>
                                </Box>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>סניף</Typography>
                                    <Typography sx={styles.infoValue}>
                                        {orderData?.branchName && orderData?.branchDescription 
                                            ? `${orderData.branchName} - ${orderData.branchDescription}`
                                            : orderData?.branchDescription || orderData?.branchName || '-'}
                                    </Typography>
                                </Box>
                                <Box sx={styles.infoItem}>
                                    <Typography sx={{ ...styles.infoLabel, color: !receivingWarehouse.trim() && !createdDocNo ? '#ef4444' : undefined }}>
                                        מחסן מקבל *
                                    </Typography>
                                    <TextField
                                        size="small"
                                        value={receivingWarehouse}
                                        onChange={(e) => {
                                            setReceivingWarehouse(e.target.value);
                                            if (error === 'יש להזין מחסן מקבל לפני יצירת קבלת סחורה') setError('');
                                        }}
                                        disabled={!!createdDocNo}
                                        placeholder="הקלד קוד מחסן"
                                        error={!receivingWarehouse.trim() && !createdDocNo}
                                        sx={styles.warehouseInput}
                                    />
                                </Box>
                                {invoice?.booknum && (
                                    <Box sx={styles.infoItem}>
                                        <Typography sx={styles.infoLabel}>מספר חשבונית</Typography>
                                        <Typography sx={styles.infoValue}>{invoice.booknum}</Typography>
                                    </Box>
                                )}
                                {orderData?.netPrice != null && (
                                    <Box sx={styles.infoItem}>
                                        <Typography sx={styles.infoLabel}>סכום הזמנה ללא מע"מ</Typography>
                                        <Typography sx={styles.infoValue}>{formatCurrency(orderData.netPrice)}</Typography>
                                    </Box>
                                )}
                                {orderData?.totalPrice != null && (
                                    <Box sx={styles.infoItem}>
                                        <Typography sx={styles.infoLabel}>סכום הזמנה כולל מע"מ</Typography>
                                        <Typography sx={{ ...styles.infoValue, color: '#1d4ed8', fontWeight: 700 }}>
                                            {formatCurrency(orderData.totalPrice)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        <Paper sx={styles.tableCard}>
                            <Box sx={styles.tableHeader}>
                                <Typography sx={styles.tableTitle}>
                                    <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    פריטים
                                </Typography>
                                <Box sx={styles.tableHeaderRight}>
                                    <Typography sx={styles.rowCount}>
                                        {activeItems.length} מתוך {items.length} שורות נבחרו
                                    </Typography>
                                </Box>
                            </Box>

                            <TableContainer sx={styles.tableContainer}>
                                <Table sx={styles.table} stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...styles.tableHeaderCell, width: 48, px: 1 }} padding="checkbox">
                                                <Checkbox
                                                    checked={items.length > 0 && deletedItems.size === 0}
                                                    indeterminate={deletedItems.size > 0 && deletedItems.size < items.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setDeletedItems(new Set());
                                                        } else {
                                                            setDeletedItems(new Set(items.map((_, i) => i)));
                                                        }
                                                    }}
                                                    disabled={!!createdDocNo || items.length === 0}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={styles.tableHeaderCell}>מק"ט</TableCell>
                                            <TableCell sx={styles.tableHeaderCell}>תיאור מוצר</TableCell>
                                            <TableCell sx={styles.tableHeaderCell}>כמות</TableCell>
                                            <TableCell sx={styles.tableHeaderCell}>מחיר ליחידה</TableCell>
                                            <TableCell sx={styles.tableHeaderCell}>סה"כ מחיר</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6}>
                                                    <Box sx={styles.emptyState}>
                                                        <InventoryIcon sx={styles.emptyIcon} />
                                                        <Typography sx={styles.emptyText}>
                                                            אין פריטים בהזמנה
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            items.map((item, index) => {
                                                const isDeleted = deletedItems.has(index);
                                                const rowTotal = item.userModified
                                                    ? (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)
                                                    : (parseFloat(item.totalPrice) || 0);

                                                return (
                                                    <TableRow
                                                        key={index}
                                                        sx={styles.tableRow}
                                                        className={isDeleted ? 'deleted' : ''}
                                                    >
                                                        <TableCell sx={{ ...styles.tableCell, width: 48, px: 1 }} padding="checkbox">
                                                            <Checkbox
                                                                checked={!isDeleted}
                                                                onChange={() => isDeleted ? handleRestoreItem(index) : handleDeleteItem(index)}
                                                                disabled={!!createdDocNo}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={styles.tableCell}>
                                                            {item.itemCode}
                                                        </TableCell>
                                                        <TableCell sx={styles.tableCell}>
                                                            {item.description}
                                                        </TableCell>
                                                        <TableCell sx={{ ...styles.tableCell, ...styles.editableCell }}>
                                                            <TextField
                                                                type="number"
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                size="small"
                                                                sx={styles.editInput}
                                                                disabled={isDeleted || !!createdDocNo}
                                                                inputProps={{ min: 0, step: 0.01 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ ...styles.tableCell, ...styles.editableCell }}>
                                                            <TextField
                                                                type="number"
                                                                value={item.price}
                                                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                                size="small"
                                                                sx={styles.editInput}
                                                                disabled={isDeleted || !!createdDocNo}
                                                                inputProps={{ min: 0, step: 0.01 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={styles.tableCell}>
                                                            {formatCurrency(rowTotal)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        <Paper sx={styles.actionsContainer}>
                            <Box sx={styles.summaryBox}>
                                <Box sx={styles.summaryItem}>
                                    <Typography sx={styles.summaryLabel}>סה"כ שורות:</Typography>
                                    <Typography sx={styles.summaryValue}>{activeItems.length}</Typography>
                                </Box>
                                <Box sx={styles.summaryItem}>
                                    <Typography sx={styles.summaryLabel}>סה"כ ללא מע"מ:</Typography>
                                    <Typography sx={styles.summaryValue}>{formatCurrency(total)}</Typography>
                                </Box>
                                <Box sx={styles.summaryItem}>
                                    <Typography sx={styles.summaryLabel}>סה"כ כולל מע"מ:</Typography>
                                    <Typography sx={{ ...styles.summaryValue, color: '#1d4ed8' }}>{formatCurrency(totalWithVat)}</Typography>
                                </Box>
                            </Box>

                            <Box sx={styles.actionsButtons}>
                                <Button
                                    variant="outlined"
                                    onClick={onBack}
                                    sx={styles.cancelButton}
                                    disabled={isCreating}
                                >
                                    ביטול
                                </Button>
                                
                                <Button
                                    variant="contained"
                                    onClick={handleCreateGoodsReceipt}
                                    sx={styles.saveDraftButton}
                                    disabled={isCreating || activeItems.length === 0 || !receivingWarehouse.trim()}
                                    startIcon={isCreating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                                >
                                    {isCreating ? 'יוצר קבלת סחורה...' : 'צור קבלת סחורה'}
                                </Button>
                            </Box>
                        </Paper>

                        {isCreating && (
                            <Box sx={styles.loadingOverlay}>
                                <Box sx={styles.loadingContent}>
                                    <CircularProgress />
                                    <Typography sx={styles.loadingText}>
                                        יוצר קבלת סחורה ומאשר בפריוריטי...
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {showInvoicePanel && (
                        <Box sx={styles.viewerPanel}>
                            <Box sx={styles.viewerPanelHeader}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <DescriptionIcon sx={{ color: '#3b82f6', fontSize: '1.3rem' }} />
                                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                        חשבונית
                                        {invoice?.booknum && (
                                            <Box component="span" sx={{ color: '#3b82f6', mr: 0.5 }}> — {invoice.booknum}</Box>
                                        )}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={() => setShowInvoicePanel(false)}
                                    size="small"
                                    sx={{ color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' } }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Box sx={styles.viewerPanelContent}>
                                {invoiceViewerError ? (
                                    <Alert severity="error" sx={{ m: 2 }}>{invoiceViewerError}</Alert>
                                ) : (
                                    <FileViewer
                                        fileAsBase64={invoiceContent}
                                        fileName={invoice?.fileName}
                                        isLoadingFile={isLoadingInvoice}
                                        height="100%"
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </ThemeProvider>
        </CacheProvider>
    );
};

export default GoodsReceiptCreationScreen;
